import { SubmissionPlacementTaskInfo } from '@common/types';
import { findLast } from 'lodash';
import moment from 'moment';
import { Stack, Typography } from '@common-components';
import { PlacementTask, PlacementTaskStatus } from 'enums';
import { messages } from 'i18n';
import { SubmissionSearchItem } from 'types';
import { PlacementTaskChip } from './PlacementTaskChip';

interface PlacementTaskStatusDisplayProps {
  placementTasks: SubmissionPlacementTaskInfo[];
  submission: SubmissionSearchItem;
}

function calculateDaysUntilTrigger(dueDate: Date, bookDaysBeforeEffectiveDate: number): number {
  // Calculate trigger date: dueDate - bookDaysBeforeEffectiveDate
  const triggerDate = moment(dueDate).subtract(bookDaysBeforeEffectiveDate, 'days');
  // Calculate days from now until trigger date
  return triggerDate.diff(moment().startOf('day'), 'days');
}

function CompletedTasksDisplay({ lastCompleted }: { lastCompleted?: SubmissionPlacementTaskInfo }) {
  return (
    <Stack>
      <Typography variant="body2">{messages.placementProcess.allTasksCompleted}</Typography>
      {lastCompleted?.completedAt && (
        <Typography variant="caption" color="text.secondary">
          {messages.placementProcess.completedAt(new Date(lastCompleted.completedAt))}
        </Typography>
      )}
    </Stack>
  );
}

function PendingPlacementDisplay({ submission }: { submission: SubmissionSearchItem }) {
  const { dueDate, bookDaysBeforeEffectiveDate } = submission;

  if (!dueDate || bookDaysBeforeEffectiveDate === undefined) {
    return (
      <Stack>
        <Typography variant="body2">{messages.placementProcess.allTasksPlanned}</Typography>
        <Typography variant="caption" color="text.secondary">
          {messages.placementProcess.upcomingTriggerDateNoInfo}
        </Typography>
      </Stack>
    );
  }

  const daysUntilTrigger = calculateDaysUntilTrigger(dueDate, bookDaysBeforeEffectiveDate);
  const message =
    daysUntilTrigger >= 0
      ? messages.placementProcess.upcomingTriggerDate(daysUntilTrigger)
      : messages.placementProcess.missingWorkflow;

  return (
    <Stack>
      <Typography variant="body2">{messages.placementProcess.allTasksPlanned}</Typography>
      <Typography variant="caption" color="text.secondary">
        {message}
      </Typography>
    </Stack>
  );
}

function NextPlannedTaskDisplay({ task }: { task: SubmissionPlacementTaskInfo }) {
  const taskName = messages.placementProcess.placementTasks[task.placementTask as PlacementTask];

  return (
    <Stack>
      <Typography variant="body2">{messages.placementProcess.nextPlannedTask}</Typography>
      <Typography variant="caption" color="text.secondary">
        {taskName}
      </Typography>
    </Stack>
  );
}

export function PlacementTaskStatusDisplay({ placementTasks, submission }: PlacementTaskStatusDisplayProps) {
  const inProgress = placementTasks.find((task) => task.status === PlacementTaskStatus.InProgress);

  if (inProgress) {
    return <PlacementTaskChip task={inProgress} />;
  }

  const allCompleted = placementTasks.every(
    (task) => task.status === PlacementTaskStatus.Completed || task.status === PlacementTaskStatus.Skipped,
  );

  if (allCompleted) {
    const lastCompleted = findLast(
      placementTasks,
      (task): task is SubmissionPlacementTaskInfo => task.status === PlacementTaskStatus.Completed,
    );

    return <CompletedTasksDisplay lastCompleted={lastCompleted} />;
  }

  const allPlanned = placementTasks.every((task) => task.status === PlacementTaskStatus.Planned);

  if (allPlanned) {
    return <PendingPlacementDisplay submission={submission} />;
  }

  const nextPlanned = placementTasks.find((task) => task.status === PlacementTaskStatus.Planned);

  if (nextPlanned) {
    return <NextPlannedTaskDisplay task={nextPlanned} />;
  }

  return null;
}
